import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

const BALKAN_COUNTRY_CODES = new Set(['HR', 'RS', 'BA', 'MK', 'SI', 'ME']);
const BALKAN_TIMEZONES = new Set([
  'Europe/Zagreb',
  'Europe/Belgrade',
  'Europe/Sarajevo',
  'Europe/Skopje',
  'Europe/Ljubljana',
  'Europe/Podgorica',
]);

const getLocaleCountryCode = (locale = '') => {
  const parts = String(locale).split(/[-_]/);
  if (parts.length < 2) return null;
  return (parts[1] || '').toUpperCase();
};

const getRegionalDefaultLanguage = () => {
  if (typeof window === 'undefined') return 'en';

  const preferredLocales = [
    ...(Array.isArray(navigator.languages) ? navigator.languages : []),
    navigator.language,
  ].filter(Boolean);

  const hasBalkanLocale = preferredLocales.some((locale) => {
    const country = getLocaleCountryCode(locale);
    return country && BALKAN_COUNTRY_CODES.has(country);
  });

  if (hasBalkanLocale) return 'hr';

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (BALKAN_TIMEZONES.has(timezone)) return 'hr';

  return 'en';
};

const resources = {
  en: {
    translation: {
      app_name: 'Bereket',
      tagline: 'Increase Barakah in Your Daily Life',
      get_started: 'Get Started',
      login: 'Login',
      register: 'Register',
      email: 'Email',
      password: 'Password',
      name: 'Name',
      logout: 'Logout',
      dashboard: 'Dashboard',
      prayers: 'Prayers',
      sadaqah: 'Sadaqah',
      good_deeds: 'Good Deeds',
      dhikr: 'Dhikr',
      duas: 'Duas',
      daily_inspiration: 'Daily Inspiration',
      profile: 'Profile',
      settings: 'Settings',
      language: 'Language',
      today: 'Today',
      this_week: 'This Week',
      this_month: 'This Month',
      total: 'Total',
      add: 'Add',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      loading: 'Loading...',
      welcome_back: 'Welcome back',
      prayer_tracker: 'Prayer Tracker',
      fajr: 'Fajr',
      dhuhr: 'Dhuhr',
      asr: 'Asr',
      maghrib: 'Maghrib',
      isha: 'Isha',
      prayed: 'Prayed',
      not_prayed: 'Not Prayed',
      mark_as_prayed: 'Mark as Prayed',
      sadaqah_tracker: 'Sadaqah Tracker',
      amount: 'Amount',
      description: 'Description',
      date: 'Date',
      add_sadaqah: 'Add Sadaqah',
      good_deeds_log: 'Good Deeds Log',
      title: 'Title',
      add_good_deed: 'Add Good Deed',
      dhikr_counter: 'Dhikr Counter',
      count: 'Count',
      reset: 'Reset',
      increment: 'Increment',
      daily_verse: 'Daily Verse',
      daily_hadith: 'Daily Hadith',
      dua_collection: 'Dua Collection',
      or_continue_with: 'Or continue with',
      google_login: 'Continue with Google',
      already_have_account: 'Already have an account?',
      dont_have_account: "Don't have an account?",
      your_spiritual_journey: 'Your Spiritual Journey',
      track_organize_grow: 'Track, organize, and grow in your faith',
      todays_barakah: "Today's Barakah",
      starting_the_journey: 'Starting the Journey',
      total_points: 'total points',
      activities_logged: 'activities logged',
      todays_quran: "Today's Quran",
      read: 'Read',
      ayahs: 'ayahs',
      listened: 'Listened',
      memorized: 'Memorized',
      quick_stats: 'Quick Stats',
      quran: 'Quran',
      barakah: 'Barakah',
      pts: 'pts',
      open_source_multilingual: 'Open source • Multilingual • Privacy-focused',
      feat_prayer_desc: 'Track your 5 daily prayers',
      feat_sadaqah_desc: 'Record your charity and giving',
      feat_good_deeds_desc: 'Log your good deeds and kindness',
      feat_dhikr_desc: 'Count your daily remembrance',
      feat_quran_desc: 'Read, search and track your Quran progress',
      feat_hadith_desc: 'Browse and save daily hadiths',
      feat_journal_desc: 'Write your reflections and intentions',
      feat_planner_desc: 'Plan your daily spiritual activities',
      journal: 'Journal',
      planner: 'Planner',
      hadith: 'Hadith',
      profile_settings: 'Profile & Settings',
      account: 'Account',
      change_password: 'Change Password',
      current_password: 'Current Password',
      new_password: 'New Password',
      confirm_new_password: 'Confirm New Password',
      update_password: 'Update Password',
      password_updated: 'Password updated successfully',
      password_error: 'Error updating password',
      passwords_no_match: 'Passwords do not match',
      notifications: 'Notifications',
      notification_settings: 'Notification Settings',
      prayer_reminders: 'Prayer Reminders',
      prayer_reminders_desc: 'Get reminded before each prayer time',
      daily_dhikr: 'Daily Dhikr Reminder',
      daily_dhikr_desc: 'Reminder to complete your daily dhikr',
      daily_verse_notif: 'Daily Verse',
      daily_verse_notif_desc: 'Receive a daily Quran verse',
      appearance: 'Appearance',
      dark_mode: 'Dark Mode',
      dark_mode_desc: 'Switch to a darker color scheme',
      language_settings: 'Language',
      language_settings_desc: 'Choose your preferred language',
      app_language: 'Application Language',
      english: 'English',
      croatian: 'Croatian (Bosanski)',
      danger_zone: 'Danger Zone',
      sign_out: 'Sign Out',
      sign_out_desc: 'Log out of your account on this device',
      settings_saved: 'Settings saved',
      more_coming_soon: 'More settings coming soon',
      more_coming_soon_desc: 'Additional customization options will be available in future updates.',
      hi: 'Hi',
      member_since: 'Member since',
    },
  },
  hr: {
    translation: {
      app_name: 'Bereket',
      tagline: 'Povećajte Bereket u Svakodnevnom Životu',
      get_started: 'Počnite',
      login: 'Prijava',
      register: 'Registracija',
      email: 'Email',
      password: 'Lozinka',
      name: 'Ime',
      logout: 'Odjava',
      dashboard: 'Nadzorna ploča',
      prayers: 'Namazi',
      sadaqah: 'Sadaka',
      good_deeds: 'Dobra djela',
      dhikr: 'Zikir',
      duas: 'Dove',
      daily_inspiration: 'Dnevna inspiracija',
      profile: 'Profil',
      settings: 'Postavke',
      language: 'Jezik',
      today: 'Danas',
      this_week: 'Ovaj tjedan',
      this_month: 'Ovaj mjesec',
      total: 'Ukupno',
      add: 'Dodaj',
      save: 'Spremi',
      cancel: 'Odustani',
      delete: 'Obriši',
      edit: 'Uredi',
      loading: 'Učitavanje...',
      welcome_back: 'Dobrodošli natrag',
      prayer_tracker: 'Praćenje namaza',
      fajr: 'Sabah',
      dhuhr: 'Podne',
      asr: 'Ikindija',
      maghrib: 'Akšam',
      isha: 'Jacija',
      prayed: 'Obavljeno',
      not_prayed: 'Nije obavljeno',
      mark_as_prayed: 'Označi kao obavljeno',
      sadaqah_tracker: 'Praćenje sadake',
      amount: 'Iznos',
      description: 'Opis',
      date: 'Datum',
      add_sadaqah: 'Dodaj sadaku',
      good_deeds_log: 'Dnevnik dobrih djela',
      title: 'Naslov',
      add_good_deed: 'Dodaj dobro djelo',
      dhikr_counter: 'Brojač zikira',
      count: 'Broj',
      reset: 'Resetiraj',
      increment: 'Povećaj',
      daily_verse: 'Dnevni ajet',
      daily_hadith: 'Dnevni hadis',
      dua_collection: 'Zbirka dova',
      or_continue_with: 'Ili nastavite s',
      google_login: 'Nastavite s Googleom',
      already_have_account: 'Već imate račun?',
      dont_have_account: 'Nemate račun?',
      your_spiritual_journey: 'Vaš duhovni put',
      track_organize_grow: 'Pratite, organizirajte i rastite u vjeri',
      todays_barakah: 'Bereket Danas',
      starting_the_journey: 'Početak puta',
      total_points: 'ukupno bodova',
      activities_logged: 'aktivnosti zabilježeno',
      todays_quran: "Kur'an Danas",
      read: 'Pročitano',
      ayahs: 'ajeta',
      listened: 'Preslušano',
      memorized: 'Naučeno',
      quick_stats: 'Brzi pregled',
      quran: "Kur'an",
      barakah: 'Bereket',
      pts: 'bodova',
      open_source_multilingual: 'Otvoreni kod • Višejezičan • Usmjeren na privatnost',
      feat_prayer_desc: 'Pratite svojih 5 dnevnih namaza',
      feat_sadaqah_desc: 'Bilježite svoju sadaku i davanje',
      feat_good_deeds_desc: 'Zapisujte dobra djela i dobrotu',
      feat_dhikr_desc: 'Brojite dnevni zikir',
      feat_quran_desc: "Čitajte, pretražujte i pratite napredak u Kur'anu",
      feat_hadith_desc: 'Pregledajte i pohranite dnevne hadise',
      feat_journal_desc: 'Pišite svoja razmišljanja i nijete',
      feat_planner_desc: 'Planirajte dnevne duhovne aktivnosti',
      journal: 'Dnevnik',
      planner: 'Planer',
      hadith: 'Hadisi',
      profile_settings: 'Profil i postavke',
      account: 'Račun',
      change_password: 'Promijeni lozinku',
      current_password: 'Trenutna lozinka',
      new_password: 'Nova lozinka',
      confirm_new_password: 'Potvrdi novu lozinku',
      update_password: 'Ažuriraj lozinku',
      password_updated: 'Lozinka uspješno ažurirana',
      password_error: 'Greška pri ažuriranju lozinke',
      passwords_no_match: 'Lozinke se ne podudaraju',
      notifications: 'Obavijesti',
      notification_settings: 'Postavke obavijesti',
      prayer_reminders: 'Podsjetnici za namaz',
      prayer_reminders_desc: 'Primajte podsjetnike prije svakog vremena namaza',
      daily_dhikr: 'Dnevni podsjetnik za zikir',
      daily_dhikr_desc: 'Podsjetnik za obavljanje dnevnog zikira',
      daily_verse_notif: 'Dnevni ajet',
      daily_verse_notif_desc: "Primajte dnevni ajet iz Kur'ana",
      appearance: 'Izgled',
      dark_mode: 'Tamni način',
      dark_mode_desc: 'Prebacite na tamniju shemu boja',
      language_settings: 'Jezik',
      language_settings_desc: 'Odaberite željeni jezik aplikacije',
      app_language: 'Jezik aplikacije',
      english: 'Engleski',
      croatian: 'Bosanski / Hrvatski',
      danger_zone: 'Opasna zona',
      sign_out: 'Odjava',
      sign_out_desc: 'Odjavite se sa svog računa na ovom uređaju',
      settings_saved: 'Postavke spremljene',
      more_coming_soon: 'Više postavki uskoro',
      more_coming_soon_desc: 'Dodatne opcije prilagodbe bit će dostupne u budućim ažuriranjima.',
      hi: 'Zdravo',
      member_since: 'Član od',
    },
  },
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: localStorage.getItem('language') || getRegionalDefaultLanguage(),
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;
